import { createSlice } from '@reduxjs/toolkit';
import { MESSAGE_STATUS } from 'configs/constants';
import { enableMapSet } from 'immer';

enableMapSet();


const initialState = {
  timer: 0,
  chatRequests: new Set(),
  chats: new Map(),
  watchingChats: new Map(),
};

const pendingFileValue = (file) => {
  if (!file) return '';
  if (typeof file === 'string') return file;
  try {
    return URL.createObjectURL(file);
  } catch {
    return '';
  }
};

const chatKey = (id) => (id == null ? id : String(id));
const getChatById = (map, id) => {
  if (!map || id == null) return undefined;
  return map.get(id) || map.get(String(id));
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateTimer: (state)=> {
      state.timer++;
    },
    setNewChat: (state, action)=> {
      const chat = action.payload.chat;
      const id = chatKey(chat._id);
      let oldMessages = [];
      let listUnreadMsg = {};

      const data = [...state.chats.values()].find(
        (item) => String(item.companion._id) === String(chat.companion._id)
      );
      if(data) {
        state.chats.delete(data._id);
        state.chats.delete(chatKey(data._id));
        oldMessages = data.messages;
      }

      for (let item of action.payload.visitorUnread) {
        listUnreadMsg[item] = true;
      }

      state.chats = state.chats.set(
        id,
        {...chat, _id: id, messages: [...oldMessages, ...action.payload.messages[0]], unreadCount: action.payload.unreadCount, visitorUnread: listUnreadMsg, chatOpen: data?.chatOpen, undeliveredMessages: {}}
      );
    },
    setUndeliveredMessage: (state, action)=> {
      const {chatId, message, hashId, file} = action.payload;
      const id = chatKey(chatId);
      const chatData = getChatById(state.chats, id);
      if(!chatData || !hashId) return;

      const localUserId = chatData.user._id;
      const undeliveredMessages = chatData.undeliveredMessages || {};
      // [id, userId, text, file, date, status] — must match delivered message.new shape
      const messageData = [
        hashId,
        localUserId,
        message == null ? '' : String(message),
        pendingFileValue(file),
        new Date().toISOString(),
        MESSAGE_STATUS.UNDELIVERED
      ];

      state.chats = state.chats.set(id, {
        ...chatData,
        undeliveredMessages: {...undeliveredMessages, [String(hashId)]: messageData}
      })
    },
    setNewMessage: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      const messageId = action.payload.message[0];
      const hashId = action.payload.hashId;
      const chatHistory = getChatById(state.chats, id);
      const watchingChatHistory = getChatById(state.watchingChats, id);

      if(chatHistory){
        const undeliveredMessages = chatHistory.undeliveredMessages || {};
        const newUndeliveredMessages = hashId
          ? Object.fromEntries(
              Object.entries(undeliveredMessages).filter(([key]) => String(key) !== String(hashId))
            )
          : undeliveredMessages;

        state.chats = state.chats.set(id, {
          ...chatHistory,
          unreadCount: chatHistory.chatOpen? 0 : ++chatHistory.unreadCount,
          messages: [...(chatHistory.messages || []), ...action.payload.message],
          undeliveredMessages: newUndeliveredMessages,
          visitorUnread: {...chatHistory.visitorUnread, [messageId]: true}
        })
      }

      if(watchingChatHistory) {
        state.watchingChats = state.watchingChats.set(id, {
          ...watchingChatHistory,
          messages: [...(watchingChatHistory.messages || []), ...action.payload.message]
        })
      }
    },
    setTyping: (state, action)=> {
      const id = chatKey(action.payload.chatId ?? action.payload._id);
      const typing = action.payload.typing;
      const chatHistory = getChatById(state.chats, id);
      if(!chatHistory) return;
      state.chats = state.chats.set(id, {
        ...chatHistory,
        typing: typing
      });
    },
    addHistory: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      const myPrevChatHistory = getChatById(state.chats, id);
      if(myPrevChatHistory) {
        state.chats = state.chats.set(
          id,
          { ...(myPrevChatHistory || []), messages: [...action.payload.messages, ...(myPrevChatHistory?.messages || [])] }
        );
        return;
      }

      const prevChatHistory = getChatById(state.watchingChats, id);
      if(prevChatHistory) {
        state.watchingChats = state.watchingChats.set(
          id,
          { ...(prevChatHistory || []), messages: [...action.payload.messages, ...(prevChatHistory?.messages || [])] }
        );
      }
    },
    endChatState: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      const myPrevChatHistory = getChatById(state.chats, id);
      if(myPrevChatHistory) {
        state.chats = state.chats.set(id, { ...(myPrevChatHistory), chatEnd: true});
      }
    },
    deleteChat: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      state.chats.delete(action.payload.chatId);
      state.chats.delete(id);
    },
    incomingChats: (state, action) => {
      for(let item of action.payload){
        state.chatRequests.add(item);
      }
    },
    deleteChatRequest: (state, action)=> {
      state.chatRequests.delete(action.payload.chatId);
    },
    updateChat: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      const chatHistory = getChatById(state.chats, id);
      if (chatHistory) {
        state.chats = state.chats.set(id, {...chatHistory, ...action.payload.data});
      }
    },
    updateWithCompId: (state, action)=> {
      const {companionId, data} = action.payload;
      const chats = [...state.chats.values()];
      const updatedItem = chats.find((item)=> String(item.companion._id) === String(companionId));
      if (!updatedItem) return;
      const id = chatKey(updatedItem._id);
      state.chats = state.chats.set(id, {...updatedItem, ...data});
    },
    messagesRead: (state, action)=> {
      const id = chatKey(action.payload);
      const chatHistory = getChatById(state.chats, id);
      
      if (chatHistory && Object.keys(chatHistory?.visitorUnread).length) {
        state.chats = state.chats.set(id, {...chatHistory, visitorUnread: {}});
      }
    },
    removeChat: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      state.chats.delete(action.payload.chatId);
      state.chats.delete(id);
    },
    addWatchingChat: (state, action)=> {
      if(action.payload.messages[0] && typeof action.payload.messages[0][0] === 'object'){
        const messages = [];
        action.payload.messages[0].reverse().forEach(message => {
          messages.push(message.id, message.userId || '', message.message, message.file, message.date)
        });
        action.payload.messages[0] = messages;
      }
      const id = chatKey(action.payload.chat._id);
      state.watchingChats = state.watchingChats.set(
        id,
        {...action.payload.chat, _id: id, messages: action.payload.messages[0] || [], unreadCount: action.payload.unreadCount || 0, chatOpen: false});
    },
    removeWatchingChat: (state, action)=> {
      const id = chatKey(action.payload.chatId);
      state.watchingChats.delete(action.payload.chatId);
      state.watchingChats.delete(id);
    },
    updateWatchingChat: (state, action)=> {
      const {chatId, user} = action.payload;
      const id = chatKey(chatId);
      const watchingChat = getChatById(state.watchingChats, id);
      if (!watchingChat) return;
      state.watchingChats = state.watchingChats.set(id, {...watchingChat, user});
    }
  }
});

export const {
  endChatState, 
  deleteChat,
  addHistory, 
  setNewMessage, 
  setUndeliveredMessage,
  setNewChat, 
  addReferrer,
  incomingChats,
  deleteChatRequest,
  updateChat,
  updateWithCompId,
  messagesRead,
  removeChat,
  addWatchingChat,
  removeWatchingChat,
  updateWatchingChat,
  updateTimer,
  setTyping
} = chatSlice.actions;
export const selectChat = (state) => state.chat;
export default chatSlice.reducer;
